import type { BadgeProps, ButtonProps, MantineTheme } from '@mantine/core';
import type { CSSProperties } from 'react';

interface QuickStatusFilterButtonAppearance {
  color: string;
  radius: ButtonProps['radius'];
  textColor: string;
  fontWeight: number;
  badgeVariant: BadgeProps['variant'];
  styles: ButtonProps['styles'];
  buttonStyle?: CSSProperties;
  countStyle?: CSSProperties;
  textStyle?: CSSProperties;
}

const mutedButtonStyle: CSSProperties = {
  borderColor: 'transparent',
  boxShadow: 'none',
};

const mutedStates = {
  active: {
    buttonStyle: {
      ...mutedButtonStyle,
      backgroundColor: 'var(--mantine-primary-color-light)',
    },
    countStyle: {
      backgroundColor: 'var(--mantine-primary-color-filled)',
      color: 'var(--mantine-primary-color-contrast)',
    },
  },
  inactive: {
    buttonStyle: { ...mutedButtonStyle, backgroundColor: 'transparent' },
    countStyle: {
      backgroundColor: 'var(--mantine-color-default-hover)',
      color: 'var(--mantine-color-dimmed)',
    },
  },
};

export const getQuickStatusFilterButtonAppearance = (
  theme: MantineTheme,
  colorScheme: 'light' | 'dark',
  active: boolean,
  muted: boolean,
  compact = false
): QuickStatusFilterButtonAppearance => {
  const palette = {
    dark: {
      border: theme.colors.dark[4],
      background: theme.colors.dark[6],
      activeBackground: theme.colors.dark[5],
      text: theme.colors.gray[2],
    },
    light: {
      border: theme.colors.gray[2],
      background: theme.white,
      activeBackground: theme.white,
      text: theme.colors.gray[7],
    },
  }[colorScheme];
  const state = active ? 'active' : 'inactive';
  const activeColor = theme.colors[`primary-${colorScheme}`][0];
  const states = {
    active: {
      color: activeColor,
      textColor: activeColor,
      fontWeight: 700,
      badgeVariant: 'filled',
      root: {
        border: `1px solid ${activeColor}`,
        backgroundColor: palette.activeBackground,
        boxShadow: '0 0 0 1px rgba(32, 201, 151, 0.08)',
      },
    },
    inactive: {
      color: 'gray',
      textColor: palette.text,
      fontWeight: 600,
      badgeVariant: 'light',
      root: {
        border: `1px solid ${palette.border}`,
        backgroundColor: palette.background,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      },
    },
  };
  const { root, ...content } = states[state];
  const appearance: QuickStatusFilterButtonAppearance = {
    ...content,
    radius: 'xl',
    styles: {
      root: { ...root, minHeight: 34 },
      inner: { justifyContent: 'space-between', width: '100%' },
      label: { width: '100%' },
    },
  };

  if (!muted) return appearance;

  const mutedAppearance: QuickStatusFilterButtonAppearance = {
    ...appearance,
    ...mutedStates[state],
    radius: 'md',
    textColor: active ? activeColor : 'var(--mantine-color-dimmed)',
    fontWeight: 500,
  };

  if (!compact) return mutedAppearance;

  return {
    ...mutedAppearance,
    fontWeight: 600,
    buttonStyle: {
      ...mutedAppearance.buttonStyle,
      padding: '5px 12px',
      height: 'auto',
      minHeight: 0,
      border: 'none',
      borderRadius: 8,
      flex: 'none',
    },
    textStyle: { fontSize: 13, lineHeight: 'normal', whiteSpace: 'nowrap' },
    countStyle: {
      ...mutedAppearance.countStyle,
      minWidth: 22,
      height: 17,
      padding: '0 7px',
      fontSize: 10,
      fontWeight: 700,
      lineHeight: 1,
      border: 'none',
    },
  };
};
